import {
  AuditEntryType,
  type CaseRecord,
  type CaseRecordAuditEntry,
} from '@compass/client/types/case_record';

import type { FormField } from '@/types/formSchema';
import { formatFieldName } from '@/utils/string';

/** Keys within output_data/input_data that are internal metadata, not user-facing fields. */
const DATA_INTERNAL_KEYS = new Set(['schema_version']);

/** Top-level field paths that are system metadata and should be hidden from diffs. */
export const HIDDEN_FIELD_PATHS = new Set(['updated_at', 'version']);

export type FieldSource = 'input_data' | 'output_data' | 'other';

export interface FlattenedField {
  fieldPath: string;
  value: unknown;
  previousValue?: unknown;
  source: FieldSource;
}

export const SOURCE_LABELS: Record<FieldSource, string> = {
  input_data: 'Input',
  output_data: 'Output',
  other: 'Other',
};

/**
 * Format a field path for display.
 * Uses the schema label when available, otherwise falls back to
 * taking the last segment of dot-separated paths and formatting.
 */
export function formatFieldPath(path: string, fieldSchemaMap?: Record<string, FormField>): string {
  const segments = path.split('.');
  const lastSegment = segments[segments.length - 1];

  if (fieldSchemaMap && lastSegment in fieldSchemaMap) {
    return fieldSchemaMap[lastSegment].label;
  }

  return formatFieldName(lastSegment);
}

/**
 * Get the FormField schema for a given field path.
 */
export function getFieldSchema(
  path: string,
  fieldSchemaMap?: Record<string, FormField>,
): FormField | undefined {
  if (!fieldSchemaMap) return undefined;
  const segments = path.split('.');
  return fieldSchemaMap[segments[segments.length - 1]];
}

/** Determine the FieldSource for a top-level key. */
function sourceForKey(key: string): FieldSource {
  if (key.startsWith('output_data')) return 'output_data';
  if (key.startsWith('input_data')) return 'input_data';
  return 'other';
}

/** Expand a data-bucket (output_data / input_data) object into individual field entries. */
function expandDataBucket(
  key: string,
  value: any,
  result: FlattenedField[],
  previousBucket?: any,
): void {
  const source = key as FieldSource;
  // CaseRecordData has { schema_version, data: { ... } } structure
  const dataObj = value.data && typeof value.data === 'object' ? value.data : value;
  const prevDataObj =
    previousBucket?.data && typeof previousBucket.data === 'object'
      ? previousBucket.data
      : previousBucket;
  for (const [fieldId, fieldValue] of Object.entries(dataObj)) {
    if (DATA_INTERNAL_KEYS.has(fieldId)) continue;
    result.push({
      fieldPath: `${key}.${fieldId}`,
      value: fieldValue,
      previousValue: prevDataObj?.[fieldId],
      source,
    });
  }
}

/**
 * Flatten updated_fields so that "output_data" / "input_data" object values
 * are expanded into individual field entries, tagged by source.
 * Optionally pairs each field with its previous value from `previousValues`.
 */
export function flattenUpdatedFields(
  fields: { [key: string]: any },
  previousValues?: Record<string, any>,
): FlattenedField[] {
  const result: FlattenedField[] = [];

  for (const [key, value] of Object.entries(fields)) {
    if (HIDDEN_FIELD_PATHS.has(key)) continue;

    const isDataBucket =
      (key === 'output_data' || key === 'input_data') && value && typeof value === 'object';
    if (isDataBucket) {
      expandDataBucket(key, value, result, previousValues?.[key]);
      continue;
    }

    result.push({
      fieldPath: key,
      value,
      previousValue: previousValues?.[key],
      source: sourceForKey(key),
    });
  }

  return result;
}

/**
 * Group flattened fields by their source.
 */
export function groupBySource(fields: FlattenedField[]): Map<FieldSource, FlattenedField[]> {
  const groups = new Map<FieldSource, FlattenedField[]>();
  for (const field of fields) {
    const existing = groups.get(field.source);
    if (existing) {
      existing.push(field);
    } else {
      groups.set(field.source, [field]);
    }
  }
  return groups;
}

// ---------------------------------------------------------------------------
// State reconstruction: compute previous values from checkpoint + delta chain
// ---------------------------------------------------------------------------

/** Deep clone a value using JSON serialization. */
function deepClone<T>(value: T): T {
  if (value === undefined || value === null) return value;
  return JSON.parse(JSON.stringify(value));
}

/** Get a value from a nested object using a dot-separated path. */
export function getNestedValue(obj: Record<string, any>, path: string): any {
  const segments = path.split('.');
  let current: any = obj;
  for (const seg of segments) {
    if (current == null || typeof current !== 'object') return undefined;
    current = current[seg];
  }
  return current;
}

/** Set a value in a nested object using a dot-separated path. */
function setNestedValue(obj: Record<string, any>, path: string, value: any): void {
  const segments = path.split('.');
  let current: any = obj;
  for (let i = 0; i < segments.length - 1; i++) {
    if (current[segments[i]] == null || typeof current[segments[i]] !== 'object') {
      current[segments[i]] = {};
    }
    current = current[segments[i]];
  }
  current[segments[segments.length - 1]] = value;
}

/** Delete a value from a nested object using a dot-separated path. */
function deleteNestedValue(obj: Record<string, any>, path: string): void {
  const segments = path.split('.');
  let current: any = obj;
  for (let i = 0; i < segments.length - 1; i++) {
    if (current[segments[i]] == null || typeof current[segments[i]] !== 'object') return;
    current = current[segments[i]];
  }
  delete current[segments[segments.length - 1]];
}

/** Deep-merge src into dst (mutates dst). Arrays and primitives are replaced, objects are merged. */
function deepMerge(dst: Record<string, any>, src: Record<string, any>): void {
  for (const [key, value] of Object.entries(src)) {
    if (
      value != null &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      dst[key] != null &&
      typeof dst[key] === 'object' &&
      !Array.isArray(dst[key])
    ) {
      deepMerge(dst[key], value);
    } else {
      dst[key] = deepClone(value);
    }
  }
}

/**
 * Extract the previous values from `state` for the leaf fields being set in `updatedFields`.
 * Returns a nested object with the same structure as updatedFields but with values from state.
 */
/** Extract a per-index sparse previous value for an array update. */
function extractPreviousForArray(stateArr: any[] | undefined, updatedArr: any[]): any[] {
  return updatedArr.map((item, idx) => {
    const stateItem = Array.isArray(stateArr) ? stateArr[idx] : undefined;
    if (
      item != null &&
      typeof item === 'object' &&
      !Array.isArray(item) &&
      stateItem != null &&
      typeof stateItem === 'object' &&
      !Array.isArray(stateItem)
    ) {
      return extractPreviousFromState(stateItem, item);
    }
    if (Array.isArray(item)) {
      return extractPreviousForArray(Array.isArray(stateItem) ? stateItem : undefined, item);
    }
    return deepClone(stateItem);
  });
}

function extractPreviousFromState(
  state: Record<string, any>,
  updatedFields: Record<string, any>,
): Record<string, any> {
  const result: Record<string, any> = {};
  for (const [key, value] of Object.entries(updatedFields)) {
    const stateValue = state[key];
    if (Array.isArray(value)) {
      result[key] = extractPreviousForArray(
        Array.isArray(stateValue) ? stateValue : undefined,
        value,
      );
    } else if (
      value != null &&
      typeof value === 'object' &&
      stateValue != null &&
      typeof stateValue === 'object' &&
      !Array.isArray(stateValue)
    ) {
      result[key] = extractPreviousFromState(stateValue, value);
    } else {
      result[key] = deepClone(stateValue);
    }
  }
  return result;
}

/** Convert a CaseRecord proto object to a plain nested map for state tracking. */
function caseRecordToStateMap(record: CaseRecord): Record<string, any> {
  const state: Record<string, any> = {};
  for (const [key, value] of Object.entries(record)) {
    if (value !== undefined && value !== null) {
      state[key] = deepClone(value);
    }
  }
  return state;
}

/** Check if an entry is a checkpoint (full document snapshot). */
function isCheckpointEntry(entry: CaseRecordAuditEntry): boolean {
  const isInsert = entry.operation?.toLowerCase() === 'insert';
  const isFull = entry.entry_type === AuditEntryType.AUDIT_ENTRY_TYPE_FULL;
  return (isInsert || isFull) && !!entry.case_record;
}

/** Extract previous values for a delta entry's changed fields from the current state. */
function extractDeltaPreviousValues(
  state: Record<string, any>,
  updatedFields: Record<string, any> | undefined,
  removedFields: string[] | undefined,
): Record<string, any> {
  const previousValues: Record<string, any> = {};

  if (updatedFields && Object.keys(updatedFields).length > 0) {
    Object.assign(previousValues, extractPreviousFromState(state, updatedFields));
  }

  if (removedFields) {
    for (const path of removedFields) {
      const oldVal = getNestedValue(state, path);
      if (oldVal !== undefined) {
        setNestedValue(previousValues, path, deepClone(oldVal));
      }
    }
  }

  return previousValues;
}

/** Apply a delta's changes to the running state (mutates state). */
function applyDeltaToState(
  state: Record<string, any>,
  updatedFields: Record<string, any> | undefined,
  removedFields: string[] | undefined,
): void {
  if (updatedFields) {
    deepMerge(state, updatedFields);
  }
  if (removedFields) {
    for (const path of removedFields) {
      deleteNestedValue(state, path);
    }
  }
}

// ---------------------------------------------------------------------------
// Structural diff: walk before/after values and surface only what changed
// ---------------------------------------------------------------------------

type ValueChangeKind = 'changed' | 'added' | 'removed';

export interface ValueChange {
  /** Path segments from the root field, e.g. ['drug', '[0]', 'specialty_pharmacy_details', 'network_status'] */
  path: string[];
  kind: ValueChangeKind;
  before?: unknown;
  after?: unknown;
}

function isEmptyValue(v: unknown): boolean {
  return v === undefined || v === null || v === '';
}

function isPlainObjectValue(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function diffArrays(before: unknown[], after: unknown[], path: string[]): ValueChange[] {
  const out: ValueChange[] = [];
  const max = Math.max(before.length, after.length);
  for (let i = 0; i < max; i++) {
    const childPath = [...path, `[${i}]`];
    if (i >= before.length) {
      out.push({ path: childPath, after: after[i], kind: 'added' });
    } else if (i >= after.length) {
      out.push({ path: childPath, before: before[i], kind: 'removed' });
    } else {
      out.push(...diffValues(before[i], after[i], childPath));
    }
  }
  return out;
}

function diffObjects(
  before: Record<string, unknown>,
  after: Record<string, unknown>,
  path: string[],
): ValueChange[] {
  const out: ValueChange[] = [];
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  for (const key of keys) {
    out.push(...diffValues(before[key], after[key], [...path, key]));
  }
  return out;
}

function diffLeaf(before: unknown, after: unknown, path: string[]): ValueChange[] {
  if (isEmptyValue(before)) return [{ path, after, kind: 'added' }];
  if (isEmptyValue(after)) return [{ path, before, kind: 'removed' }];
  if (JSON.stringify(before) !== JSON.stringify(after)) {
    return [{ path, before, after, kind: 'changed' }];
  }
  return [];
}

/**
 * Recursively diff `before` and `after`, returning a flat list of leaf-level
 * changes (only the fields whose primitive value differs). Arrays are diffed
 * by index — additions/removals at the tail surface as separate entries.
 */
export function diffValues(before: unknown, after: unknown, path: string[] = []): ValueChange[] {
  if (before === after) return [];
  if (isEmptyValue(before) && isEmptyValue(after)) return [];
  if (Array.isArray(before) && Array.isArray(after)) return diffArrays(before, after, path);
  if (isPlainObjectValue(before) && isPlainObjectValue(after)) {
    return diffObjects(before, after, path);
  }
  return diffLeaf(before, after, path);
}

/**
 * Compute a map of `entryId → previousValues` for every delta entry.
 * Processes entries oldest-to-newest, building a running state from checkpoints
 * and extracting previous values before applying each delta.
 */
export function computePreviousValuesMap(
  entries: CaseRecordAuditEntry[],
): Map<string, Record<string, any>> {
  const result = new Map<string, Record<string, any>>();
  if (entries.length === 0) return result;

  // Entries arrive newest-first; process oldest-first for state reconstruction
  const chronological = [...entries].reverse();
  let state: Record<string, any> = {};

  for (const entry of chronological) {
    if (isCheckpointEntry(entry)) {
      state = caseRecordToStateMap(entry.case_record!);
      continue;
    }

    if (entry.update_description && entry.id) {
      const { updated_fields, removed_fields } = entry.update_description;
      result.set(entry.id, extractDeltaPreviousValues(state, updated_fields, removed_fields));
      applyDeltaToState(state, updated_fields, removed_fields);
    }
  }

  return result;
}
